import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { API_BASE, getTenantBranding } from "../api/agentApi";
import {
  DEFAULT_TENANT_BRANDING,
  TenantBranding,
  normalizeTenantBranding,
  resolveTenantBranding,
} from "../branding";

const BRANDING_CACHE_KEY = "tenantBranding";

type TenantBrandingContextValue = {
  branding: TenantBranding;
  persistedBranding: TenantBranding;
  loading: boolean;
  error: string;
  refreshBranding: () => Promise<void>;
  resolveBrandAssetUrl: (url: string | null | undefined) => string | null;
};

const TenantBrandingContext = createContext<TenantBrandingContextValue | undefined>(
  undefined,
);

function readCachedBranding(): TenantBranding | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const stored = window.localStorage.getItem(BRANDING_CACHE_KEY);
    return stored ? normalizeTenantBranding(JSON.parse(stored)) : null;
  } catch (_error) {
    return null;
  }
}

function writeCachedBranding(branding: TenantBranding) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(branding));
  } catch (_error) {
    // Ignore storage failures; runtime branding still applies for this session.
  }
}

function resolveBrandAssetUrl(url: string | null | undefined): string | null {
  if (!url) {
    return null;
  }
  if (url.startsWith("/config/branding/assets/")) {
    return `${API_BASE}${url}`;
  }
  return url;
}

function applyFavicon(url: string | null | undefined) {
  if (typeof document === "undefined" || !url) {
    return;
  }
  const resolvedUrl = resolveBrandAssetUrl(url);
  let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = resolvedUrl ?? DEFAULT_TENANT_BRANDING.faviconUrl ?? "/favicon.ico";
}

export function TenantBrandingProvider({
  children,
  whiteLabelDemo = false,
}: {
  children: React.ReactNode;
  whiteLabelDemo?: boolean;
}) {
  const cachedBranding = useMemo(readCachedBranding, []);
  const [persistedBranding, setPersistedBranding] = useState<TenantBranding>(
    cachedBranding ?? DEFAULT_TENANT_BRANDING,
  );
  const [hasLoadedInitialBranding, setHasLoadedInitialBranding] = useState(
    Boolean(cachedBranding) || whiteLabelDemo,
  );
  const [loading, setLoading] = useState(!cachedBranding && !whiteLabelDemo);
  const [error, setError] = useState("");

  const refreshBranding = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getTenantBranding();
      const normalized = normalizeTenantBranding(response?.tenant_branding);
      setPersistedBranding(normalized);
      writeCachedBranding(normalized);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load tenant branding");
      setPersistedBranding(cachedBranding ?? DEFAULT_TENANT_BRANDING);
    } finally {
      setHasLoadedInitialBranding(true);
      setLoading(false);
    }
  }, [cachedBranding]);

  useEffect(() => {
    refreshBranding();
  }, [refreshBranding]);

  const branding = useMemo(
    () => resolveTenantBranding(persistedBranding, whiteLabelDemo),
    [persistedBranding, whiteLabelDemo],
  );

  useEffect(() => {
    applyFavicon(branding.faviconUrl);
    if (typeof document !== "undefined") {
      document.title = branding.brandName;
    }
  }, [branding.brandName, branding.faviconUrl]);

  const value = useMemo<TenantBrandingContextValue>(
    () => ({
      branding,
      persistedBranding,
      loading,
      error,
      refreshBranding,
      resolveBrandAssetUrl,
    }),
    [branding, error, loading, persistedBranding, refreshBranding],
  );

  return (
    <TenantBrandingContext.Provider value={value}>
      {hasLoadedInitialBranding ? children : null}
    </TenantBrandingContext.Provider>
  );
}

export function useTenantBranding() {
  const context = useContext(TenantBrandingContext);
  if (!context) {
    throw new Error("useTenantBranding must be used within a TenantBrandingProvider");
  }
  return context;
}
